"use client";

import { useEffect, useState } from "react";
import { Link2, LogIn, RefreshCw, UserRound } from "lucide-react";
import { Button } from "@nitro/ui";
import { apiFetch } from "../lib/api";
import styles from "./SimulatorPilotAccessCard.module.css";

export function SimulatorPilotAccessCard({ simulatorName, request = apiFetch }) {
  const [state, setState] = useState({ status: "loading", user: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setState({ status: "loading", user: null });
    async function load() {
      try {
        const data = await request("/api/v1/auth/me", { cache: "no-store", timeoutMs: 6000, signal: controller.signal });
        if (!data || !Object.hasOwn(data, "user") || (data.user !== null && (typeof data.user !== "object" || Array.isArray(data.user)))) throw new Error("Invalid session response");
        if (active) setState({ status: data.user ? "authenticated" : "anonymous", user: data.user || null });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setState({ status: error?.status === 401 ? "anonymous" : "error", user: null });
      }
    }
    void load();
    return () => { active = false; controller.abort(); };
  }, [attempt, request]);

  const name = displayName(state.user);
  const initials = name.split(/\s+/).filter(Boolean).filter((_, index, words) => index === 0 || index === words.length - 1).map((word) => Array.from(word)[0]).join("").toLocaleUpperCase("es-CO");
  const authenticated = state.status === "authenticated";
  const loading = state.status === "loading";
  const error = state.status === "error";

  return (
    <article aria-label="Cuenta del piloto" aria-busy={loading} className={`simulator-side-card ${styles.card}`}>
      <div aria-hidden="true" className={styles.avatar}>{authenticated ? initials : <UserRound size={24} />}</div>
      <div className={styles.identity}>
        <p className={styles.eyebrow}>{authenticated ? "Tu cuenta en Nitro" : "Tu acceso a Nitro"}</p>
        <h2>{authenticated ? name : loading ? "Consultando tu sesión…" : error ? "Sesión no disponible" : "Acceso de pilotos"}</h2>
        <p>{authenticated ? `Gestiona tu cuenta de ${simulatorName} y revisa los requisitos de inscripción.` : loading ? "Estamos comprobando tu acceso." : error ? "No pudimos consultar tu sesión. Inténtalo de nuevo." : "Inicia sesión para gestionar tus cuentas e inscribirte."}</p>
      </div>
      {error ? (
        <Button className={styles.action} iconStart={<RefreshCw size={16} />} onClick={() => setAttempt((value) => value + 1)} variant="secondary">Reintentar</Button>
      ) : loading ? (
        <Button className={styles.action} disabled loading loadingText="Comprobando…" variant="secondary">Comprobando…</Button>
      ) : (
        <Button as="a" className={styles.action} href={authenticated ? "/perfil/cuentas" : "/login"} iconStart={authenticated ? <Link2 size={16} /> : <LogIn size={16} />} variant="secondary">{authenticated ? "Mis cuentas" : "Iniciar sesión"}</Button>
      )}
      <span aria-live="polite" className={styles.status}>{loading ? "Consultando sesión" : error ? "No se pudo consultar la sesión" : authenticated ? "Sesión iniciada" : "Inicia sesión para continuar"}</span>
    </article>
  );
}

function displayName(user) {
  if (!user) return "";
  const pilot = user.pilot || {};
  const fullName = [pilot.firstName || user.firstName, pilot.lastName || user.lastName].filter(Boolean).join(" ").trim();
  return String(pilot.displayName || user.displayName || fullName || "Piloto Nitro").trim() || "Piloto Nitro";
}
